import { useQuery } from "@tanstack/react-query";
import ReactApexChart from "react-apexcharts";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { Card, CardBody, CardTitle } from "reactstrap";
import ComplianceMonitoringDashboardService from "src/modules/3rd-party-management/apis/admin/compliance/monitoring/DashboardService";
import { RisksRates } from "src/modules/3rd-party-management/constants";
import ChartError from "src/modules/3rd-party-management/pages/admin/compliance/components/dashboard/ChartError";

const RisksChart = ({ t, filters }) => {
    const fetchDataQuery = useQuery({
        queryKey: [
            "3rd-party-management-fetch-compliance-monitoring-dashboard",
            "risks-and-mitigation-tasks",
            filters
        ],
        queryFn: async () => {
            const service = ComplianceMonitoringDashboardService.getInstance();

            return await service.fetchRisksAndTasks(filters);
        },
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching dashboard data."), {
                type: "error"
            });
        }
    });

    if (fetchDataQuery.isFetching) {
        return (
            <Card>
                <CardBody>
                    <div className="dt-field dt-skeleton mb-2" style={{ width: '40%' }}></div>
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '320px' }}></div>
                </CardBody>
            </Card>
        );
    }

    const {
        complianceMonitoringRisks = {}
    } = fetchDataQuery.data || {};

    const options = {
        labels: [],
        plotOptions: {
            pie: {
                donut: {
                    size: "75%",
                },
            },
        },
        dataLabels: {
            enabled: true
        },
        legend: {
            show: true,
            position: 'bottom'
        },
        colors: []
    };

    const series = [];
    const labels = [];
    const colors = [];

    for (const index in complianceMonitoringRisks || {}) {
        const rateKey = index.toLowerCase();

        series.push(complianceMonitoringRisks[index]);
        labels.push(t(RisksRates[rateKey]?.title));
        colors.push(RisksRates[rateKey]?.color);
    }

    const isSeriesEmpty = series.every((s) => s === 0);

    return (
        <Card className="h-100">
            <CardBody>
                <div className="mb-4">
                    <CardTitle>
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}>
                            <div>
                                {t('Risk Distribution by Monitoring Results')}
                            </div>
                        </div>
                    </CardTitle>
                </div>

                <div className="mb-4">
                    <div className="apex-charts mb-3">
                        {fetchDataQuery.isError ? (
                            <ChartError
                                title={
                                    fetchDataQuery.error?.response?.status === 403 ?
                                        'Error 403' :
                                        null
                                }
                                message={
                                    fetchDataQuery.error?.response?.status === 403 ?
                                        'Please upgrade your plan to access this feature' :
                                        null
                                }
                                onRetry={fetchDataQuery.refetch}
                            />
                        ) : (
                            <ReactApexChart
                                options={{
                                    ...options,
                                    labels: isSeriesEmpty ? [t("No Data")] : labels,
                                    colors: isSeriesEmpty ? ["e0e0e0"] : colors
                                }}
                                series={isSeriesEmpty ? [1] : series}
                                type="donut"
                                height="255"
                            />
                        )}
                    </div>
                </div>
            </CardBody>
        </Card>
    );
}

export default withNamespaces()(
    RisksChart
);